import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';

const STAT_COLS = [
    { key: 'points',          label: 'PTS', hint: 'Must stay balanced across team' },
    { key: 'three_pm',        label: '3PM' },
    { key: 'two_pm',          label: '2PM' },
    { key: 'ft_made',         label: 'FTM' },
    { key: 'rebounds',        label: 'REB' },
    { key: 'assists',         label: 'AST' },
    { key: 'personal_fouls',  label: 'PF'  },
    { key: 'technical_fouls', label: 'TF'  },
];

const BLANK = { points: 0, three_pm: 0, two_pm: 0, ft_made: 0, rebounds: 0, assists: 0, personal_fouls: 0, technical_fouls: 0 };

/**
 * StatAdjustmentModal
 *
 * Props:
 *   game        — finished game row ({ id, game_date, team1, team2, team1_score, team2_score })
 *   teamId      — the captain's team id
 *   onClose     — () => void
 *   onSubmitted — () => void  called after successful submit/update
 */
const StatAdjustmentModal = ({ game, teamId, onClose, onSubmitted }) => {
    const { user } = useAuth();

    const [players,         setPlayers]         = useState([]);
    const [originalStats,   setOriginalStats]   = useState({});  // { [playerId]: statObj }
    const [draftStats,      setDraftStats]       = useState({});  // { [playerId]: statObj }
    const [existingRequest, setExistingRequest] = useState(null); // pending request if any
    const [loading,         setLoading]         = useState(true);
    const [submitting,      setSubmitting]       = useState(false);
    const [activePlayer,    setActivePlayer]    = useState(null); // playerId of expanded row on mobile

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);

            const [{ data: playersData }, { data: statsData }, { data: existingReqs }] = await Promise.all([
                supabase.from('players').select('id, name, jersey_number')
                    .eq('team_id', teamId).order('jersey_number', { ascending: true }),
                supabase.from('game_stats').select('*').eq('game_id', game.id)
                    .in('player_id', (await supabase.from('players').select('id').eq('team_id', teamId))
                        .data?.map(p => p.id) ?? []),
                supabase.from('stat_adjustment_requests')
                    .select('*')
                    .eq('game_id', game.id)
                    .eq('team_id', teamId)
                    .eq('status', 'pending')
                    .maybeSingle(),
            ]);

            if (cancelled) return;

            const statMap = {};
            (playersData || []).forEach(p => { statMap[p.id] = { ...BLANK }; });
            (statsData   || []).forEach(s => {
                statMap[s.player_id] = {
                    points:          s.points          ?? 0,
                    three_pm:        s.three_pm        ?? 0,
                    two_pm:          s.two_pm          ?? 0,
                    ft_made:         s.ft_made         ?? 0,
                    rebounds:        s.rebounds        ?? 0,
                    assists:         s.assists         ?? 0,
                    personal_fouls:  s.personal_fouls  ?? 0,
                    technical_fouls: s.technical_fouls ?? 0,
                };
            });

            setPlayers(playersData || []);
            setOriginalStats(statMap);

            // If pending request exists, start from its requested_stats so captain can edit it
            if (existingReqs) {
                setExistingRequest(existingReqs);
                setDraftStats(existingReqs.requested_stats || statMap);
            } else {
                setDraftStats(JSON.parse(JSON.stringify(statMap))); // deep clone
            }

            if (playersData?.length) setActivePlayer(playersData[0].id);
            setLoading(false);
        };
        load();
        return () => { cancelled = true; };
    }, [game.id, teamId]);

    // ── Point balance: sum of (draft.points - original.points) across all players ──
    const pointDelta = useMemo(() => {
        const getPts = (s) => 
            (Number(s?.three_pm || 0) * 3) + 
            (Number(s?.two_pm || 0) * 2) + 
            (Number(s?.ft_made || 0) * 1);

        return players.reduce((acc, p) => {
            const origTotal  = getPts(originalStats[p.id]);
            const draftTotal = getPts(draftStats[p.id]);
            return acc + (draftTotal - origTotal);
        }, 0);
    }, [players, originalStats, draftStats]);

    const isBalanced = pointDelta === 0;

    const updateStat = (playerId, field, value) => {
        setDraftStats(prev => ({
            ...prev,
            [playerId]: { ...prev[playerId], [field]: Math.max(0, Number(value)) },
        }));
    };

    const hasChanges = useMemo(() => {
        return players.some(p =>
            STAT_COLS.some(col =>
                Number(draftStats[p.id]?.[col.key] ?? 0) !== Number(originalStats[p.id]?.[col.key] ?? 0)
            )
        );
    }, [players, draftStats, originalStats]);

    const handleSubmit = async () => {
        if (!isBalanced) { return; } // button disabled anyway
        setSubmitting(true);
        try {
            // Recompute points for every player before submitting
            const normalizedStats = Object.fromEntries(
                players.map(p => {
                    const s = draftStats[p.id] || BLANK;
                    const points = (Number(s.three_pm) * 3) + (Number(s.two_pm) * 2) + Number(s.ft_made);
                    return [p.id, { ...s, points }];
                })
            );

            if (existingRequest) {
                // Update existing pending request
                const { error } = await supabase
                    .from('stat_adjustment_requests')
                    .update({ requested_stats: normalizedStats, updated_at: new Date().toISOString() })
                    .eq('id', existingRequest.id);
                if (error) throw error;
            } else {
                // Create new request
                const { error } = await supabase
                    .from('stat_adjustment_requests')
                    .insert({
                        game_id:         game.id,
                        team_id:         teamId,
                        captain_id:      user.id,
                        status:          'pending',
                        requested_stats: draftStats,
                        original_stats:  originalStats,
                    });
                if (error) throw error;
            }
            onSubmitted();
            onClose();
        } catch (err) {
            alert(`Error submitting request: ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    // Parse date without UTC shift
    const [dp] = (game.game_date || '').split('T');
    const [y, m, d] = dp.split('-').map(Number);
    const displayDate = new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'short', month: 'short', day: 'numeric',
    });

    const opponent = game.team1?.id === teamId ? game.team2?.name : game.team1?.name;
    const activePlayerData = players.find(p => p.id === activePlayer);

    return (
        <div className="fixed inset-0 bg-black/75 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
            <div className="bg-white dark:bg-gray-900 w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl
                            shadow-2xl flex flex-col max-h-[92dvh] sm:max-h-[85dvh] overflow-hidden">

                {/* ── Header ── */}
                <div className="px-5 pt-5 pb-4 border-b dark:border-gray-800 shrink-0">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                Request Stat Adjustment
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                vs {opponent} · {displayDate}
                            </p>
                        </div>
                        <button onClick={onClose}
                            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200
                                       hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0">
                            ✕
                        </button>
                    </div>

                    {/* Existing request banner */}
                    {existingRequest && (
                        <div className="mt-3 flex items-center gap-2 bg-amber-50 dark:bg-amber-900/30
                                        border border-amber-200 dark:border-amber-700 rounded-xl px-3 py-2">
                            <span className="text-amber-500 text-sm">⏳</span>
                            <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                                You have a pending request for this game. Submitting will update it.
                            </p>
                        </div>
                    )}
                </div>

                {/* ── Point balance bar ── */}
                <div className={`shrink-0 px-5 py-3 flex items-center justify-between text-sm font-medium
                                 transition-colors ${
                    isBalanced
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                        : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400'
                }`}>
                    <span>Point balance</span>
                    <span className="font-bold tabular-nums text-base">
                        {pointDelta === 0 ? '✓ Balanced' : `${pointDelta > 0 ? '+' : ''}${pointDelta} pts`}
                    </span>
                </div>

                {loading ? (
                    <div className="flex-1 flex items-center justify-center text-gray-400 py-12">
                        Loading roster…
                    </div>
                ) : (
                    <>
                        {/* ── Player selector tabs (mobile-optimised horizontal scroll) ── */}
                        <div className="shrink-0 flex gap-1.5 overflow-x-auto px-4 py-3
                                        scrollbar-none border-b dark:border-gray-800">
                            {players.map(p => {
                                const changed = STAT_COLS.some(col =>
                                    Number(draftStats[p.id]?.[col.key] ?? 0) !== Number(originalStats[p.id]?.[col.key] ?? 0)
                                );
                                return (
                                    <button
                                        key={p.id}
                                        onClick={() => setActivePlayer(p.id)}
                                        className={`shrink-0 px-3 py-2 rounded-xl text-sm font-semibold transition-all relative ${
                                            activePlayer === p.id
                                                ? 'bg-orange-600 text-white shadow-sm'
                                                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                                        }`}
                                    >
                                        #{p.jersey_number ?? '—'}
                                        {changed && (
                                            <span className={`absolute -top-1 -right-1 w-2 h-2 rounded-full
                                                              ${activePlayer === p.id ? 'bg-white' : 'bg-orange-500'}`} />
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        {/* ── Active player stat editor ── */}
                        {activePlayerData && (
                            <div className="flex-1 overflow-y-auto px-5 py-4">
                                <div className="mb-4">
                                    <p className="font-bold text-gray-900 dark:text-white text-base">
                                        {activePlayerData.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Jersey #{activePlayerData.jersey_number ?? '—'} · tap a stat to edit
                                    </p>
                                </div>

                                <div className="grid grid-cols-4 gap-3">
                                    {STAT_COLS.map(col => {
                                        const orig  = Number(originalStats[activePlayer]?.[col.key] ?? 0);
                                        const draft = Number(draftStats[activePlayer]?.[col.key]    ?? 0);
                                        const changed = draft !== orig;
                                        const isPoints = col.key === 'points';

                                        // Calculate points dynamically from current draft state
                                        const currentDraft = draftStats[activePlayer] || {};
                                        const calculatedPoints = 
                                            (Number(currentDraft.three_pm || 0) * 3) + 
                                            (Number(currentDraft.two_pm || 0) * 2) + 
                                            (Number(currentDraft.ft_made || 0) * 1);

                                        // Use the calculated value if it's the points column, otherwise use draft
                                        const displayValue = isPoints ? calculatedPoints : draft;

                                        return (
                                            <div key={col.key}
                                                className={`relative rounded-2xl border-2 p-3 transition-all ${
                                                    changed
                                                        ? isPoints
                                                            ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20'
                                                            : 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                                                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                                                }`}
                                            >
                                                <p className={`text-[10px] font-bold uppercase tracking-widest mb-2 ${
                                                    changed
                                                        ? isPoints ? 'text-orange-500' : 'text-blue-500'
                                                        : 'text-gray-400 dark:text-gray-500'
                                                }`}>
                                                    {col.label}
                                                </p>

                                                {/* Stepper */}
                                                <div className="flex items-center justify-between gap-1">
                                                    {!isPoints && (
                                                        <button
                                                            onClick={() => updateStat(activePlayer, col.key, draft - 1)}
                                                            className="w-8 h-8 rounded-xl bg-white dark:bg-gray-700 border dark:border-gray-600
                                                                    text-gray-700 dark:text-gray-200 font-bold text-lg leading-none
                                                                    flex items-center justify-center active:scale-95 transition-transform
                                                                    hover:bg-gray-100 dark:hover:bg-gray-600 disabled:opacity-40"
                                                            disabled={draft <= 0}
                                                        >
                                                            −
                                                        </button>
                                                    )}

                                                    <span className={`text-xl font-black tabular-nums ${isPoints ? 'w-full' : 'w-8'} text-center ${
                                                        changed ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                                                    }`}>
                                                        {displayValue}
                                                    </span>
                                                    
                                                    {!isPoints && (
                                                        <button
                                                            onClick={() => updateStat(activePlayer, col.key, draft + 1)}
                                                            className="w-8 h-8 rounded-xl bg-white dark:bg-gray-700 border dark:border-gray-600
                                                                    text-gray-700 dark:text-gray-200 font-bold text-lg leading-none
                                                                    flex items-center justify-center active:scale-95 transition-transform
                                                                    hover:bg-gray-100 dark:hover:bg-gray-600"
                                                        >
                                                            +
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Original value ghost */}
                                                {changed && (
                                                    <p className="text-[9px] text-center text-gray-400 dark:text-gray-500 mt-1.5 tabular-nums">
                                                        was {orig}
                                                    </p>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Points balance hint specific to this player */}
                                {(() => {
                                    const calculatePoints = (stats) => 
                                        (Number(stats?.three_pm || 0) * 3) + 
                                        (Number(stats?.two_pm || 0) * 2) + 
                                        (Number(stats?.ft_made || 0) * 1);

                                    const currentPts  = calculatePoints(draftStats[activePlayer]);
                                    const originalPts = calculatePoints(originalStats[activePlayer]);
                                    const ptsDiff     = currentPts - originalPts;

                                    if (ptsDiff === 0) return null;
                                    return (
                                        <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
                                            This player {ptsDiff > 0 ? 'gained' : 'lost'}{' '}
                                            <span className={`font-bold ${ptsDiff > 0 ? 'text-orange-500' : 'text-blue-500'}`}>
                                                {Math.abs(ptsDiff)} pt{Math.abs(ptsDiff) !== 1 ? 's' : ''}
                                            </span>
                                            {' — '}offset this on another player's PTS.
                                        </p>
                                    );
                                })()}
                            </div>
                        )}
                    </>
                )}

                {/* ── Footer ── */}
                <div className="shrink-0 border-t dark:border-gray-800 px-5 py-4 flex gap-3">
                    <button onClick={onClose}
                        className="flex-1 py-3.5 rounded-2xl border dark:border-gray-700
                                   text-gray-700 dark:text-gray-300 font-semibold text-sm
                                   hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting || !hasChanges || !isBalanced || loading}
                        className="flex-2 flex-grow-[2] py-3.5 rounded-2xl font-semibold text-sm
                                   bg-orange-600 hover:bg-orange-700 active:bg-orange-800
                                   disabled:bg-gray-300 dark:disabled:bg-gray-700
                                   disabled:text-gray-400 dark:disabled:text-gray-500
                                   text-white transition-colors"
                    >
                        {submitting
                            ? 'Submitting…'
                            : !isBalanced
                                ? `Balance points first (${pointDelta > 0 ? '+' : ''}${pointDelta})`
                                : existingRequest
                                    ? 'Update Request'
                                    : 'Submit Request'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default StatAdjustmentModal;